(function initWebTranslateV2Bootstrap() {
  const TEXT_NODE = Node.TEXT_NODE;
  const ELEMENT_NODE = Node.ELEMENT_NODE;

  const DEFAULTS = {
    version: '2.1.2',
    apiEndpoint: '',
    apiKey: '',
    sourceLang: 'en',
    targetLang: 'en',
    switcher: '#language-selector',

    elementsToTranslate: '*:not(script, style, noscript, iframe, template)',
    elementsToTranslatePlaceholders: 'input[placeholder], textarea[placeholder], [placeholder]',
    elementsToTranslateValues: 'input[type="button"], input[type="submit"], input[type="reset"]',

    skipSelector: '.no-translate, .no-translate *, [data-role="timer"], [data-role="timer"] *, [data-wt-ignore], [data-wt-ignore] *',

    caching: true,
    cacheTtlMinutes: 24 * 60,
    debug: false,

    // Событие от Vue-бриджа (webTranslateBridge.ts)
    eventName: 'wt:content-updated',

    // Debounce для очереди "динамических" (MutationObserver + bridge)
    subtreeDebounceMs: 250,
    subtreeMaxWaitMs: 1500,

    minRequestIntervalMs: 500,
    requestDedupWindowMs: 2000,
    revalidateIntervalMs: 5 * 60 * 1000,

    // Защита от "шумных" узлов (таймеры, счётчики), которых не поймал skipSelector
    rateLimitWindowMs: 3000,
    rateLimitMaxTriggers: 120,
    rateLimitBlacklistMs: 30000,

    onTranslateComplete: function onTranslateComplete() {},
    onResetTranslationComplete: function onResetTranslationComplete() {},
  };

  function createWidget(userOptions = {}) {
    const settings = {...DEFAULTS, ...userOptions};

    const SELECTED_LANGUAGE_KEY = 'wt-selected-language';

    const originalTextByNode = new WeakMap();
    const originalPlaceholderByNode = new WeakMap();
    const originalValueByNode = new WeakMap();

    const inFlight = {id: 0};
    let lastAppliedRequestId = 0;
    let currentLang = String(settings.targetLang || settings.sourceLang).toLowerCase();

    let subtreeTimer = null;
    let subtreeFirstQueuedAt = 0;
    const subtreeQueue = new Set();

    let initializedFromStorage = false;
    let lastRequestAt = 0;
    const recentSignatures = new Map();
    const lastServerSyncAtByLang = new Map();

    // Guard от собственных мутаций (withMutationGuard)
    let isApplyingTranslations = false;

    // Circuit-breaker для "шумных" узлов, которые не покрыл skipSelector
    const rateLimitState = new WeakMap();
    let blacklistedRoots = new WeakMap();

    let isSwitchingLanguage = false;

    function log(...args) {
      if (settings.debug) {
        console.log('[WebTranslate v2]', ...args);
      }
    }

    function toArray(x) {
      return x ? Array.from(x) : [];
    }

    function normalizeText(text) {
      if (typeof text !== 'string') return '';
      return text.replace(/\u00A0/g, ' ').trim();
    }

    function normalizeLookupKey(text) {
      return normalizeText(text).toLocaleLowerCase();
    }

    function isSkippableElement(el) {
      return !(el instanceof Element) || el.matches(settings.skipSelector) || el.closest(settings.skipSelector) !== null;
    }

    function isNumericLikeNonTranslatable(text) {
      return /^(?:(?:[$€£¥₽₹₴₸₺₼₾₿]|[A-Z]{3})\s*)?[+\\-]?\d+(?:[ ]?\d{3})*(?:[.,]\d+)?\s*%?(?:\s*(?:[$€£¥₽₹₴₸₺₼₾₿]|[A-Z]{3}))?$/iu.test(text);
    }

    function isEmailLikeNonTranslatable(text) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/u.test(text);
    }

    function isCssLikeNonTranslatable(text) {
      if (!text) return false;
      if (text.includes('@keyframes')) return true;
      const hasBlocks = text.includes('{') && text.includes('}');
      const hasCssSyntax = text.includes(':') && text.includes(';');
      if (hasBlocks && hasCssSyntax) return true;
      return /\b(?:position|z-index|transform|opacity|animation|background|font-family|border-radius|box-shadow)\s*:/iu.test(text);
    }

    function shouldTranslateText(text) {
      const t = normalizeText(text);
      if (!t || t.length < 2) return false;
      if (isNumericLikeNonTranslatable(t)) return false;
      if (isEmailLikeNonTranslatable(t)) return false;

      return !isCssLikeNonTranslatable(t);
    }

    // ---------------- localStorage ----------------
    // Один ключ на язык: wt-translation-<lang> = { [pathname]: { translated, timestamp, expiresAt } }

    function storageKeyForLang(lang) {
      return `wt-translation-${lang}`;
    }

    function readLangStorage(lang) {
      try {
        const raw = window.localStorage.getItem(storageKeyForLang(lang));
        return raw ? JSON.parse(raw) : {};
      } catch {
        return {};
      }
    }

    function writeLangStorage(lang, data) {
      try {
        window.localStorage.setItem(storageKeyForLang(lang), JSON.stringify(data));
      } catch {
        // ignore
      }
    }

    function readCache(lang) {
      if (!settings.caching) return null;

      const store = readLangStorage(lang);
      const path = window.location.pathname;
      const entry = store[path];
      if (!entry) return null;

      if (Date.now() > entry.expiresAt) {
        delete store[path];
        writeLangStorage(lang, store);
        return null;
      }

      return entry.translated ? {translated: entry.translated} : null;
    }

    function writeCache(lang, data) {
      if (!settings.caching || !data?.translated) return;

      const store = readLangStorage(lang);
      const path = window.location.pathname;

      store[path] = {
        translated: data.translated,
        timestamp: Date.now(),
        expiresAt: Date.now() + settings.cacheTtlMinutes * 60 * 1000,
      };

      writeLangStorage(lang, store);
    }

    function getCachedOriginalTextSet(cached) {
      const set = new Set();
      const translated = cached?.translated || {};
      Object.values(translated).forEach((row) => {
        if (row?.originalText) set.add(String(row.originalText));
      });
      return set;
    }

    function hasAllTextsInCache(texts, cached) {
      if (!settings.caching || !cached?.translated) return false;
      const cachedSet = getCachedOriginalTextSet(cached);
      return texts.every((text) => cachedSet.has(text));
    }

    function shouldRevalidate(lang) {
      const lastSync = lastServerSyncAtByLang.get(lang) || 0;
      return Date.now() - lastSync >= settings.revalidateIntervalMs;
    }

    // ---------------- Сбор / применение / сброс перевода ----------------

    function queryTargets(root, selector) {
      if (root === document.body) {
        return toArray(document.querySelectorAll(selector));
      }
      const self = root instanceof Element && root.matches(selector) ? [root] : [];
      return self.concat(toArray(root.querySelectorAll(selector)));
    }

    function collectFromRoot(root) {
      if (!(root instanceof Element) && root !== document.body) {
        return {texts: []};
      }

      const texts = [];
      const seen = new Set();

      queryTargets(root, settings.elementsToTranslate).forEach((el) => {
        if (isSkippableElement(el)) return;

        toArray(el.childNodes).forEach((node) => {
          if (node.nodeType !== TEXT_NODE) return;

          const raw = node.textContent || '';
          // ВАЖНО: если узел уже переводился ранее, используем сохранённый
          // ОРИГИНАЛ (английский), а не текущий textContent. Иначе при
          // повторном сканировании уже переведённого поддерева (например,
          // родительский Vue-компонент перерендерился из-за не связанного
          // с переводом изменения состояния — открытие дропдауна и т.п.)
          // мы отправим на сервер уже ПЕРЕВЕДЁННЫЙ текст как "оригинал",
          // получим несовпадение с кэшем/API и откатим перевод на английский.
          const hasKnownOriginal = originalTextByNode.has(node);
          const original = hasKnownOriginal ? originalTextByNode.get(node) : raw;
          const text = normalizeText(original);
          if (!shouldTranslateText(text)) return;

          if (!hasKnownOriginal) {
            originalTextByNode.set(node, raw);
          }

          if (!seen.has(text)) {
            seen.add(text);
            texts.push(text);
          }
        });
      });

      queryTargets(root, settings.elementsToTranslatePlaceholders).forEach((el) => {
        if (isSkippableElement(el)) return;

        const placeholder = el.getAttribute('placeholder');
        const hasKnownOriginal = originalPlaceholderByNode.has(el);
        const original = hasKnownOriginal ? originalPlaceholderByNode.get(el) : (placeholder || '');
        const text = normalizeText(original);
        if (!shouldTranslateText(text)) return;

        if (!hasKnownOriginal) {
          originalPlaceholderByNode.set(el, placeholder || '');
        }

        if (!seen.has(text)) {
          seen.add(text);
          texts.push(text);
        }
      });

      queryTargets(root, settings.elementsToTranslateValues).forEach((el) => {
        if (!(el instanceof HTMLInputElement) || isSkippableElement(el)) return;

        const value = el.value || '';
        const hasKnownOriginal = originalValueByNode.has(el);
        const original = hasKnownOriginal ? originalValueByNode.get(el) : value;
        const text = normalizeText(original);
        if (!shouldTranslateText(text)) return;

        if (!hasKnownOriginal) {
          originalValueByNode.set(el, value);
        }

        if (!seen.has(text)) {
          seen.add(text);
          texts.push(text);
        }
      });

      return {texts};
    }

    function buildTranslationMap(translatedMap) {
      const map = new Map();
      Object.values(translatedMap || {}).forEach((row) => {
        if (row?.originalText && row?.translatedText && row.originalText !== row.translatedText) {
          map.set(normalizeLookupKey(row.originalText), row.translatedText);
        }
      });
      return map;
    }

    function applyCachedTranslationsToRoot(root) {
      if (currentLang === settings.sourceLang) return;
      if (!(root instanceof Element) && root !== document.body) return;

      const cached = readCache(currentLang);
      if (!cached?.translated) return;

      applyTranslations(root, cached.translated);
    }

    function applyTranslatedValue(original, translationMap) {
      const lookupKey = normalizeLookupKey(original);
      const translated = translationMap.get(lookupKey);

      if (!translated) return null;

      const normalized = normalizeText(original);
      if (original.includes(normalized)) {
        return original.replace(normalized, translated);
      }
      return translated;
    }

    function withMutationGuard(fn) {
      isApplyingTranslations = true;
      try {
        fn();
      } finally {
        queueMicrotask(() => {
          isApplyingTranslations = false;
        });
      }
    }

    function applyTranslations(root, translatedMap) {
      const translationMap = buildTranslationMap(translatedMap);
      if (!translationMap.size) return;

      withMutationGuard(() => {
        queryTargets(root, settings.elementsToTranslate).forEach((el) => {
          if (isSkippableElement(el)) return;

          toArray(el.childNodes).forEach((node) => {
            if (node.nodeType !== TEXT_NODE) return;

            const hasKnownOriginal = originalTextByNode.has(node);
            const original = hasKnownOriginal ? originalTextByNode.get(node) : (node.textContent ?? '');

            if (!hasKnownOriginal) {
              originalTextByNode.set(node, original);
            }

            const next = applyTranslatedValue(original, translationMap);
            if (typeof next === 'string' && next !== node.textContent) {
              node.textContent = next;
            }
          });
        });

        queryTargets(root, settings.elementsToTranslatePlaceholders).forEach((el) => {
          if (isSkippableElement(el)) return;

          const hasKnownOriginal = originalPlaceholderByNode.has(el);
          const original = hasKnownOriginal ? originalPlaceholderByNode.get(el) : (el.getAttribute('placeholder') ?? '');

          if (!hasKnownOriginal) {
            originalPlaceholderByNode.set(el, original);
          }

          const next = applyTranslatedValue(original, translationMap);
          if (typeof next === 'string' && next !== (el.getAttribute('placeholder') ?? '')) {
            el.setAttribute('placeholder', next);
          }
        });

        queryTargets(root, settings.elementsToTranslateValues).forEach((el) => {
          if (!(el instanceof HTMLInputElement) || isSkippableElement(el)) return;

          const hasKnownOriginal = originalValueByNode.has(el);
          const original = hasKnownOriginal ? originalValueByNode.get(el) : (el.value ?? '');

          if (!hasKnownOriginal) {
            originalValueByNode.set(el, original);
          }

          const next = applyTranslatedValue(original, translationMap);
          if (typeof next === 'string' && next !== el.value) {
            el.value = next;
          }
        });
      });
    }

    function resetRoot(root = document.body) {
      withMutationGuard(() => {
        queryTargets(root, settings.elementsToTranslate).forEach((el) => {
          toArray(el.childNodes).forEach((node) => {
            if (node.nodeType !== TEXT_NODE) return;
            const original = originalTextByNode.get(node);
            if (typeof original === 'string' && node.textContent !== original) {
              node.textContent = original;
            }
          });
        });

        queryTargets(root, settings.elementsToTranslatePlaceholders).forEach((el) => {
          const original = originalPlaceholderByNode.get(el);
          if (typeof original === 'string') el.setAttribute('placeholder', original);
        });

        queryTargets(root, settings.elementsToTranslateValues).forEach((el) => {
          if (!(el instanceof HTMLInputElement)) return;
          const original = originalValueByNode.get(el);
          if (typeof original === 'string') el.value = original;
        });
      });

      settings.onResetTranslationComplete(true);
    }

    async function fetchTranslationsBatched(texts, targetLang) {
      if (!texts.length) return {translated: {}};

      const normalizedTexts = Array.from(new Set(texts)).sort();
      const signature = `${targetLang}|${normalizedTexts.join('||')}`;
      const now = Date.now();

      for (const [key, ts] of recentSignatures.entries()) {
        if (now - ts > settings.requestDedupWindowMs) {
          recentSignatures.delete(key);
        }
      }

      const cached = readCache(targetLang);

      if (hasAllTextsInCache(normalizedTexts, cached) && !shouldRevalidate(targetLang)) {
        return {translated: cached?.translated || {}};
      }

      if (recentSignatures.has(signature) && cached?.translated) {
        return {translated: cached.translated};
      }

      if (now - lastRequestAt < settings.minRequestIntervalMs) {
        const waitMs = settings.minRequestIntervalMs - (now - lastRequestAt);
        await new Promise((resolve) => window.setTimeout(resolve, waitMs));
      }

      const cacheVersions = {};
      if (cached?.translated) {
        Object.values(cached.translated).forEach((row) => {
          if (row?.originalText && Number.isInteger(row?.cache_version)) {
            cacheVersions[row.originalText] = row.cache_version;
          }
        });
      }

      const items = normalizedTexts.map((text, index) => ({id: `t_${index}`, text}));
      const reqId = ++inFlight.id;

      const headers = {'Content-Type': 'application/json'};
      if (settings.apiKey) {
        headers.Authorization = `Bearer ${settings.apiKey}`;
      }

      const response = await fetch(settings.apiEndpoint, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          sourceLang: settings.sourceLang,
          targetLang,
          items,
          cache_versions: cacheVersions,
          caching: settings.caching,
          page_url: window.location.href,
        }),
      });

      lastRequestAt = Date.now();
      recentSignatures.set(signature, lastRequestAt);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const json = await response.json();

      if (reqId < lastAppliedRequestId) {
        return {translated: cached?.translated || {}};
      }
      lastAppliedRequestId = reqId;

      const translated = json?.data?.translated || {};
      const merged = {
        translated: {
          ...(cached?.translated || {}),
          ...translated,
        },
      };

      writeCache(targetLang, merged);
      lastServerSyncAtByLang.set(targetLang, Date.now());

      return merged;
    }

    async function translateSubtree(root) {
      if (!(root instanceof Element) && root !== document.body) return;
      if (currentLang === settings.sourceLang) return;

      const {texts} = collectFromRoot(root);
      if (!texts.length) return;

      try {
        const data = await fetchTranslationsBatched(texts, currentLang);
        applyTranslations(root, data.translated);
      } catch (e) {
        log('translateSubtree error', e);
      }
    }

    async function resetPage() {
      resetRoot(document.body);
    }

    async function translatePage() {
      try {
        await resetPage();
        await translateSubtree(document.body);
        settings.onTranslateComplete(true);
      } catch (e) {
        log('translatePage error', e);
        settings.onTranslateComplete(false);
      }
    }

    async function setLanguage(lang) {
      const normalized = String(lang || settings.sourceLang).toLowerCase();
      currentLang = normalized;

      try {
        window.localStorage.setItem(SELECTED_LANGUAGE_KEY, normalized);
      } catch {
        // ignore
      }

      // Сбрасываем "шумовые" ограничители и очередь - требуется полный перевод страницы.
      isSwitchingLanguage = true;
      blacklistedRoots = new WeakMap();
      subtreeQueue.clear();
      if (subtreeTimer) {
        window.clearTimeout(subtreeTimer);
        subtreeTimer = null;
      }
      subtreeFirstQueuedAt = 0;

      try {
        if (normalized === settings.sourceLang) {
          await resetPage();
        } else {
          await translatePage();
          await new Promise((resolve) => window.setTimeout(resolve, 300));
          await translateSubtree(document.body);
        }
      } finally {
        isSwitchingLanguage = false;
      }
    }

    async function translateText(text, targetLang) {
      const t = normalizeText(text);
      const lang = String(targetLang || currentLang).toLowerCase();
      if (!shouldTranslateText(t) || lang === settings.sourceLang) return text;

      try {
        const data = await fetchTranslationsBatched([t], lang);
        const rows = Object.values(data?.translated || {});
        const row = rows.find((r) => normalizeLookupKey(r?.originalText || '') === normalizeLookupKey(t));
        return row?.translatedText || text;
      } catch (e) {
        log('translateText error', e);
        return text;
      }
    }

    // ---------------- Очередь динамических ----------------
    // Используется и Vue-бриджем (wt:content-updated), и MutationObserver'ом.

    function isDescendantOfQueued(el) {
      for (const queuedRoot of subtreeQueue) {
        if (queuedRoot !== el && queuedRoot.contains(el)) return true;
      }
      return false;
    }

    function addRootToQueue(root) {
      if (!(root instanceof Element) || !root.isConnected) return;
      if (isSkippableElement(root)) return;
      if (isDescendantOfQueued(root)) return;

      // Удаляем из очереди узлы, которые теперь являются потомками нового root
      for (const queuedRoot of Array.from(subtreeQueue)) {
        if (queuedRoot !== root && root.contains(queuedRoot)) {
          subtreeQueue.delete(queuedRoot);
        }
      }

      subtreeQueue.add(root);
      if (!subtreeFirstQueuedAt) {
        subtreeFirstQueuedAt = Date.now();
      }
    }

    async function flushSubtreeQueue() {
      subtreeTimer = null;
      subtreeFirstQueuedAt = 0;

      if (currentLang === settings.sourceLang) {
        subtreeQueue.clear();
        return;
      }

      const roots = Array.from(subtreeQueue);
      subtreeQueue.clear();
      if (!roots.length) return;

      const allTextsSet = new Set();
      const rootToTexts = new Map();

      roots.forEach((root) => {
        if (!root.isConnected) return;
        const {texts} = collectFromRoot(root);
        if (!texts.length) return;
        rootToTexts.set(root, texts);
        texts.forEach((t) => allTextsSet.add(t));
      });

      const allTexts = Array.from(allTextsSet);
      if (!allTexts.length) return;

      try {
        const data = await fetchTranslationsBatched(allTexts, currentLang);

        rootToTexts.forEach((_texts, root) => {
          if (root.isConnected) {
            applyTranslations(root, data.translated);
          }
        });
      } catch (e) {
        log('flushSubtreeQueue error', e);
      }
    }

    function scheduleSubtreeTranslate(root) {
      if (currentLang === settings.sourceLang) return;
      if (!(root instanceof Element)) return;

      addRootToQueue(root);

      if (subtreeTimer) return;

      const elapsedSinceFirst = subtreeFirstQueuedAt ? Date.now() - subtreeFirstQueuedAt : 0;
      const delay = elapsedSinceFirst >= settings.subtreeMaxWaitMs ? 0 : settings.subtreeDebounceMs;

      subtreeTimer = window.setTimeout(flushSubtreeQueue, delay);
    }

    // ---------------- Circuit breaker для "шумных" узлов ----------------
    function isRootTemporarilyBlacklisted(root) {
      const until = blacklistedRoots.get(root);
      if (!until) return false;
      if (Date.now() > until) {
        blacklistedRoots.delete(root);
        return false;
      }
      return true;
    }

    function registerRootTrigger(root) {
      const now = Date.now();
      const state = rateLimitState.get(root) || {count: 0, windowStart: now};

      if (now - state.windowStart > settings.rateLimitWindowMs) {
        state.count = 0;
        state.windowStart = now;
      }

      state.count += 1;
      rateLimitState.set(root, state);

      if (state.count > settings.rateLimitMaxTriggers) {
        blacklistedRoots.set(root, now + settings.rateLimitBlacklistMs);
        log('Root temporarily excluded from translation (too many mutations):', root);
        return false;
      }

      return true;
    }

    // ---------------- MutationObserver ----------------
    function elementFromMutationTarget(node) {
      if (node instanceof Element) return node;
      if (node && node.parentElement) return node.parentElement;
      return null;
    }

    function handleMutations(mutations) {
      if (isApplyingTranslations) return;
      if (currentLang === settings.sourceLang) return;

      mutations.forEach((mutation) => {
        const candidates = [];

        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType !== ELEMENT_NODE && node.nodeType !== TEXT_NODE) return;
            const el = elementFromMutationTarget(node);
            if (el) candidates.push(el);
          });
        } else if (mutation.type === 'characterData') {
          const el = elementFromMutationTarget(mutation.target);
          if (el) candidates.push(el);
        }

        candidates.forEach((el) => {
          if (isSkippableElement(el)) return;

          // Сначала мгновенно переводим то, что уже есть в кэше
          applyCachedTranslationsToRoot(el);

          // Во время смены языка не применяем circuit-breaker
          if (!isSwitchingLanguage) {
            if (isRootTemporarilyBlacklisted(el)) return;
            if (!registerRootTrigger(el)) return;
          }

          scheduleSubtreeTranslate(el);
        });
      });
    }

    let mutationObserver = null;

    function initMutationObserver() {
      mutationObserver = new MutationObserver(handleMutations);
      mutationObserver.observe(document.body, {
        childList: true,
        subtree: true,
        characterData: true,
      });
    }

    // ---------------- Bridge (Vue) ----------------
    function bindBridgeEvent() {
      window.addEventListener(settings.eventName, (event) => {
        if (currentLang === settings.sourceLang) return;

        const detail = event?.detail || {};
        const roots = Array.isArray(detail.roots)
            ? detail.roots
            : (detail.root instanceof Element ? [detail.root] : []);

        roots.forEach((root) => {
          if (!(root instanceof Element)) return;
          if (isSkippableElement(root)) return;

          // Мгновенно применяем известные переводы (в т.ч. к пересозданным узлам VButton), затем — обычная очередь под circuit-breaker.
          applyCachedTranslationsToRoot(root);

          if (!isSwitchingLanguage) {
            if (isRootTemporarilyBlacklisted(root)) return;
            if (!registerRootTrigger(root)) return;
          }

          scheduleSubtreeTranslate(root);
        });
      });
    }

    // ---------------- Switcher / cross-tab sync ----------------
    function bindSwitcher() {
      const switcher = document.querySelector(settings.switcher);
      if (!switcher) return;

      switcher.addEventListener('change', (event) => {
        const value = String(event?.target?.value || settings.sourceLang).toLowerCase();
        setLanguage(value).then(() => {});
      });

      try {
        const fromStorage = window.localStorage.getItem(SELECTED_LANGUAGE_KEY);
        if (fromStorage) {
          switcher.value = fromStorage;
          initializedFromStorage = true;
          setLanguage(fromStorage).then(() => {});
        }
      } catch {
        // ignore
      }
    }

    function bindStorageSync() {
      window.addEventListener('storage', (event) => {
        if (event.key !== SELECTED_LANGUAGE_KEY) return;

        const lang = String(event.newValue || '').toLowerCase();
        if (!lang) return;

        const switcher = document.querySelector(settings.switcher);
        if (switcher) switcher.value = lang;

        setLanguage(lang).then(() => {});
      });
    }

    function init() {
      log('init', settings.version);

      // Observer запускаем всегда: внутри он сразу выходит
      initMutationObserver();
      bindBridgeEvent();
      bindSwitcher();
      bindStorageSync();

      const runInitialTranslate = () => {
        if (!initializedFromStorage && currentLang !== settings.sourceLang) {
          translatePage().then(() => {});
        }
      };

      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', runInitialTranslate);
      } else {
        runInitialTranslate();
      }
    }

    init();

    return {
      setLanguage,
      translatePage,
      translateSubtree,
      resetPage,
      translateText,
      translateElement: scheduleSubtreeTranslate,
    };
  }

  const options = typeof window.wTranslateOptionsV2 === 'object'
      ? window.wTranslateOptionsV2
      : {};

  window.wTranslateV2 = createWidget(options);
})();